'use strict'

import MenuItemList from '@/app/service/api/model/menu-item-list'
import AbstractService from '@/app/service/abstract-service'
import * as types from '@/store/types'
import ClientCreateResponse from '@/app/service/api/model/client-create-response'
import ClientStatusResponse from '@/app/service/api/model/client-status-response'

const CONFIRM_ADDRESS_TOO_MUCH_ATTEMPTS_CODE = 9

class ApiService extends AbstractService {
    constructor(http, store, pageManager) {
        super()
        this.$http = http
        this.$store = store
        this.$pageManager = pageManager
    }

    getLogo() {
        const id = this.$store.state.app.id
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'get',
                url: 'logo',
                headers: {
                    'X-Smartix-Static-Key': id
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    getSetup(apiKey) {
        const referer = this.$store.state.app.referer
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'get',
                url: '/setup',
                withCredentials: true,
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    getRegistratorMenu(apiKey, registratorId) {
        const referer = this.$store.state.app.referer
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'get',
                url: '/registrator-menu',
                params: {
                    registratorId
                },
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    validatePreregistration(apiKey, phone, email, queueId, day) {
        const referer = this.$store.state.app.referer
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'get',
                url: '/validate/pre-registration',
                params: {
                    phone,
                    email,
                    queueId,
                    day
                },
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    getPreRegistrationSchedule(locationId, queueId, day, apiKey) {
        const referer = this.$store.state.app.referer
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'get',
                url: '/pre-registration-schedule',
                params: {
                    locationId,
                    queueId,
                    day
                },
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    getPreRegistrationSchedulesCount(locationId, queueId, apiKey) {
        const referer = this.$store.state.app.referer
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'get',
                url: '/pre_registration_schedules_count',
                params: {
                    locationId,
                    queueId
                },
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    save(entity, apiKey, authInfo) {
        const referer = this.$store.state.app.referer
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'post',
                url: `/pre-registration${authInfo}`,
                data: {
                    ...entity
                },
                withCredentials: true,
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    generateConfirmCode(registratorId, apiKey, phone, email, authInfo, captcha) {
        const referer = this.$store.state.app.referer

        let params = `registratorId=${encodeURIComponent(registratorId)}`
        if (phone) {
            params += `&phone=${encodeURIComponent(phone)}`
        }
        if (email) {
            params += `&email=${encodeURIComponent(email)}`
        }
        if (captcha) {
            params += `&captcha=${encodeURIComponent(captcha)}`
        }
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'post',
                url: `/generate-confirm-code${authInfo}?${params}`,
                withCredentials: true,
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    resendConfirmCode(apiKey, registratorId, transport, authInfo) {
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'post',
                url: `/confirm/${transport}/resend${authInfo}?registratorId=${registratorId}`,
                withCredentials: true,
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': this.$store.state.app.referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    verifyConfirmCodes(entity, apiKey, authInfo) {
        const referer = this.$store.state.app.referer
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'post',
                url: `/pre-registration/verify-codes${authInfo}`,
                data: {
                    ...entity
                },
                withCredentials: true,
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    captchaCheck(apiKey, answer, authInfo) {
        const referer = this.$store.state.app.referer

        return new Promise((resolve, reject) => {
            this.$http({
                method: 'get',
                url: `/captcha/validate${authInfo}?captcha=${answer}`,
                headers: {
                    'X-Smartix-Key': apiKey,
                    'X-Smartix-Referer': referer
                }
            }).then(({data}) => {
                resolve(data)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    getMenu() {
        const id = this.$store.state.app.id
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'get',
                url: 'menu',
                headers: {
                    'X-Smartix-Static-Key': id
                }
            }).then(({data}) => {
                const menuItemsList = new MenuItemList(data)
                resolve(menuItemsList)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    create(queueCode, clientPhone) {
        const id = this.$store.state.app.id
        this._setGuiLoading()

        return new Promise((resolve, reject) => {
            this.$http({
                method: 'post',
                url: 'client',
                headers: {
                    'X-Smartix-Static-Key': id
                },
                data: {
                    queueCode: queueCode,
                    clientPhone: clientPhone
                }
            }).then(({data}) => {
                const clientCreateResponse = new ClientCreateResponse(data)
                this._unsetGuiLoading()
                resolve(clientCreateResponse)
            }).catch((e) => {
                this._catchError(e)
                this._unsetGuiLoading()
            })
        })
    }

    getStatus(idClient) {
        const id = this.$store.state.app.id
        return new Promise((resolve, reject) => {
            this.$http({
                method: 'get',
                url: `client/${idClient}`,
                headers: {
                    'X-Smartix-Static-Key': id
                }
            }).then(({data}) => {
                const clientStatusResponse = new ClientStatusResponse(data)
                resolve(clientStatusResponse)
            }).catch((e) => {
                reject(e)
            })
        })
    }

    _setGuiLoading() {
        this.$store.dispatch(types.GUI.loading.set, true)
    }

    _unsetGuiLoading() {
        this.$store.dispatch(types.GUI.loading.set, false)
    }

    _catchError(err) {
        let message
        let code
        if (err && typeof err === 'string') {
            message = err
        }
        if (err && typeof err === 'object' && err.response && err.response.data) {
            const data = err.response.data
            if (data && data.message && data.description) {
                message = `${data.message}. ${data.description}`
            }
            if (data && data.code) {
                code = data.code
            }
        }

        this.$store.dispatch(types.GUI.error.set, {
            show: true,
            message: message,
            showMainPage: code === CONFIRM_ADDRESS_TOO_MUCH_ATTEMPTS_CODE
        })
    }

    _showWarning(message) {
        this.$store.dispatch(types.GUI.warning.set, {
            show: true,
            message: message
        })
    }
}

export default ApiService
